import { FillLayer, LineLayer, SymbolLayer } from 'mapbox-gl';

const airspaceLargeFill: FillLayer = {
  id: 'airspace-large-fill',
  type: 'fill',
  source: 'airspaces',
  filter: ['all', ['==', '$type', 'Polygon'], ['>=', 'area', 0.14]],
  paint: {
    'fill-color': {
      base: 1,
      type: 'categorical',
      property: 'style_category',
      stops: [
        ['controlled', '#2962FF'],
        ['env', '#4CAF50'],
        ['restricted', '#FF4081'],
        ['airport', '#37474F']
      ],
      default: '#B0BEC5'
    },
    'fill-opacity': {
      base: 1,
      stops: [[7, .05], [9, 0]]
    }
  }
};
const airspaceLargeLabel: SymbolLayer = {
  id: 'airspace-large-label',
  type: 'symbol',
  source: 'airspaces',
  filter: [
    'all',
    ['==', '$type', 'Polygon'],
    ['>=', 'area', 0.14]
  ],
  layout: {
    'text-size': 12,
    'icon-image': {
      base: 1,
      type: 'categorical',
      property: 'icon',
      stops: [
        ['env', 'park-15'],
        ['mil', 'police-15'],
        ['adp', 'airport-15']
      ]
    },
    'symbol-avoid-edges': true,
    'symbol-spacing': 250,
    'text-font': ['Rubik Medium', 'Arial Unicode MS Regular'],
    'symbol-placement': ['step', ['zoom'], 'point', 9, 'line'] as any,
    'text-padding': 0,
    visibility: 'visible',
    'text-offset': ['interpolate', ['linear'], ['zoom'], 8, ['literal', [0, 0]], 9, ['literal', [0, 0.6]]],
    'text-rotation-alignment': 'map',
    'text-anchor': {
      base: 1,
      stops: [
        [8, 'top'],
        [9, 'center']
      ]
    },
    'text-field': '{name}',
    'text-max-width': 10
  },
  paint: {
    'text-color': {
      base: 1,
      type: 'categorical',
      property: 'style_category',
      stops: [
        ['controlled', '#2962FF'],
        ['env', '#4CAF50'],
        ['restricted', '#FF4081'],
        ['airport', '#37474F'],
        ['misc', '#37474F']
      ],
      default: '#B0BEC5'
    },
    'text-halo-color': 'hsl(0, 0%, 100%)',
    'text-halo-blur': 1,
    'text-opacity': 1,
    'text-halo-width': {
      base: 1,
      stops: [
        [9, 1.5],
        [10, 2.5]
      ]
    },
    'icon-opacity': {
      base: 1,
      stops: [
        [8.99, 1],
        [9, 0]
      ]
    },
    'text-translate': ['interpolate', ['linear'], ['zoom'], 8, ['literal', [0, 15]], 9, ['literal', [0, 0]]]
  }
};
const airspaceLargeBorder: LineLayer = {
  id: 'airspace-large-border',
  type: 'line',
  source: 'airspaces',
  filter: ['all', ['==', '$type', 'Polygon'], ['>=', 'area', 0.14], ['!in', 'airspace_type', 'FAA']],
  paint: {
    'line-width': {
      base: 1,
      stops: [
        [7, 1.25],
        [9, 2]
      ]
    },
    'line-color': {
      base: 1,
      type: 'categorical',
      property: 'style_category',
      stops: [
        ['controlled', '#2962FF'],
        ['env', '#4CAF50'],
        ['restricted', '#FF4081'],
        ['airport', '#37474F'],
        ['misc', '#37474F']
      ],
      default: '#B0BEC5'
    }
  }
};
const airspaceLargeBorderBand: LineLayer = {
  id: 'airspace-large-border-band',
  type: 'line',
  source: 'airspaces',
  filter: ['all', ['==', '$type', 'Polygon'], ['>=', 'area', 0.14], ['!in', 'airspace_type', 'FAA']],
  paint: {
    'line-color': {
      base: 1,
      type: 'categorical',
      property: 'style_category',
      stops: [
        ['controlled', '#2962FF'],
        ['env', '#4CAF50'],
        ['restricted', '#FF4081'],
        ['airport', '#37474F'],
        ['misc', '#37474F']
      ],
      default: '#B0BEC5'
    },
    'line-width': {
      base: 1,
      stops: [
        [5, 0],
        [11, 10]
      ]
    },
    'line-blur': 0,
    'line-gap-width': 0,
    'line-opacity': {
      base: 1,
      stops: [
        [5, 0],
        [11, 0.15]
      ]
    },
    'line-offset': {
      base: 1,
      stops: [
        [5, 0],
        [11, 5]
      ]
    }
  }
};

const airspaceMiddleFill: FillLayer = {
  id: 'airspace-middle-fill',
  type: 'fill',
  source: 'airspaces',
  filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'style_category', 'coordinated'], ['<', 'area', 0.14], ['>', 'area', 0.02]],
  paint: {
    'fill-color': {
      base: 1,
      type: 'categorical',
      property: 'style_category',
      stops: [
        ['controlled', '#2962FF'],
        ['env', '#4CAF50'],
        ['restricted', '#FF4081'],
        ['airport', '#37474F'],
        ['misc', '#37474F']
      ],
      default: '#B0BEC5'
    },
    'fill-opacity': {
      base: 1,
      stops: [[5, 0], [9, .05], [11, 0]]
    }
  }
};
const airspaceMiddleLabel: SymbolLayer = {
  id: 'airspace-middle-label',
  type: 'symbol',
  source: 'airspaces',
  filter: [
    'all',
    ['==', '$type', 'Polygon'],
    ['!in', 'airspace_type', 'FAA'],
    ['<', 'area', 0.14], ['>', 'area', 0.02]
  ],
  layout: {
    'text-size': {
      base: 1,
      stops: [[7, 9], [9, 12]]
    },
    'icon-image': {
      base: 1,
      type: 'categorical',
      property: 'icon',
      stops: [
        ['env', 'park-15'],
        ['mil', 'police-15'],
        ['adp', 'airport-15']
      ]
    },
    'symbol-avoid-edges': true,
    'symbol-spacing': 250,
    'text-font': ['Rubik Medium', 'Arial Unicode MS Regular'],
    'symbol-placement': ['step', ['zoom'], 'point', 11, 'line'] as any,
    'text-padding': 0,
    visibility: 'visible',
    'text-offset': ['interpolate', ['linear'], ['zoom'], 9, ['literal', [0, 0]], 10, ['literal', [0, 0.6]]],
    'text-rotation-alignment': 'map',
    'text-anchor': {
      base: 1,
      stops: [
        [8, 'top'],
        [9, 'center']
      ]
    },
    'text-field': '{name}',
    'text-max-width': 10
  },
  paint: {
    'text-color': {
      base: 1,
      type: 'categorical',
      property: 'style_category',
      stops: [
        ['controlled', '#2962FF'],
        ['env', '#4CAF50'],
        ['restricted', '#FF4081'],
        ['airport', '#37474F'],
        ['misc', '#37474F']
      ],
      default: '#B0BEC5'
    },
    'text-halo-color': 'hsl(0, 0%, 100%)',
    'text-halo-blur': 1,
    'text-opacity': 1,
    'text-halo-width': {
      base: 1,
      stops: [[9, 1.5], [10, 2.5]]
    },
    'icon-opacity': {
      base: 1,
      stops: [[8.99, 1], [9, 0]]
    },
    'text-translate': ['interpolate', ['linear'], ['zoom'], 9, ['literal', [0, 10]], 10, ['literal', [0, 0]]]
  }
};
const airspaceMiddleBorder: LineLayer = {
  id: 'airspace-middle-border',
  type: 'line',
  source: 'airspaces',
  filter: ['all', ['==', '$type', 'Polygon'], ['<', 'area', 0.14], ['>', 'area', 0.02], ['!in', 'airspace_type', 'FAA']],
  paint: {
    'line-width': {
      base: 1,
      stops: [[5, .25], [11, 2]]
    },
    'line-opacity': {
      base: 1,
      stops: [[5, .25], [11, 1]]
    },
    'line-color': {
      base: 1,
      type: 'categorical',
      property: 'style_category',
      stops: [
        ['controlled', '#2962FF'],
        ['env', '#4CAF50'],
        ['restricted', '#FF4081'],
        ['airport', '#37474F'],
        ['misc', '#37474F']
      ],
      default: '#B0BEC5'
    }
  }
};
const airspaceMiddleBorderBand: LineLayer = {
  id: 'airspace-middle-border-band',
  type: 'line',
  source: 'airspaces',
  filter: ['all', ['==', '$type', 'Polygon'], ['<', 'area', 0.14], ['>', 'area', 0.02], ['!in', 'airspace_type', 'FAA']],
  paint: {
    'line-color': {
      base: 1,
      type: 'categorical',
      property: 'style_category',
      stops: [
        ['controlled', '#2962FF'],
        ['env', '#4CAF50'],
        ['restricted', '#FF4081'],
        ['airport', '#37474F'],
        ['misc', '#37474F']
      ],
      default: '#B0BEC5'
    },
    'line-width': {
      base: 1,
      stops: [
        [7, 0],
        [9, 10]
      ]
    },
    'line-blur': 0,
    'line-gap-width': 0,
    'line-opacity': {
      base: 1,
      stops: [
        [7, 0],
        [9, 0.15]
      ]
    },
    'line-offset': {
      base: 1,
      stops: [
        [7, 0],
        [9, 5]
      ]
    }
  }
};

const airspaceSmallFill: FillLayer = {
  id: 'airspace-small-fill',
  type: 'fill',
  source: 'airspaces',
  filter: ['all', ['==', '$type', 'Polygon'], ['<=', 'area', 0.02]],
  paint: {
    'fill-color': {
      base: 1,
      type: 'categorical',
      property: 'style_category',
      stops: [
        ['controlled', '#2962FF'],
        ['env', '#4CAF50'],
        ['restricted', '#FF4081'],
        ['airport', '#37474F'],
        ['misc', '#37474F']
      ],
      default: '#B0BEC5'
    },
    'fill-opacity': {
      base: 1,
      stops: [[7, 0], [9, .05], [13, 0]]
    }
  }
};
const airspaceSmallLabel: SymbolLayer = {
  id: 'airspace-small-label',
  type: 'symbol',
  source: 'airspaces',
  filter: [
    'all',
    ['==', '$type', 'Polygon'],
    ['!in', 'airspace_type', 'FAA'],
    ['<', 'area', 0.02]
  ],
  layout: {
    'text-size': {
      base: 1,
      stops: [[11, 9], [13, 12]]
    },
    'icon-image': {
      base: 1,
      type: 'categorical',
      property: 'icon',
      stops: [
        ['env', 'park-15'],
        ['mil', 'police-15'],
        ['adp', 'airport-15']
      ]
    },
    'symbol-avoid-edges': true,
    'symbol-spacing': 250,
    'text-font': ['Rubik Medium', 'Arial Unicode MS Regular'],
    'symbol-placement': ['step', ['zoom'], 'point', 13, 'line'] as any,
    'text-padding': 0,
    visibility: 'visible',
    'text-offset': [0, 0],
    'text-rotation-alignment': 'map',
    'text-anchor': {
      base: 1,
      stops: [
        [11, 'top'],
        [13, 'center']
      ]
    },
    'text-keep-upright': false,
    'text-field': {
      base: 1,
      stops: [
        [8, ''],
        [9, '{name}']
      ]
    },
    'text-max-width': 10
  },
  paint: {
    'text-color': {
      base: 1,
      type: 'categorical',
      property: 'style_category',
      stops: [
        ['controlled', '#2962FF'],
        ['env', '#4CAF50'],
        ['restricted', '#FF4081'],
        ['airport', '#37474F'],
        ['misc', '#37474F']
      ],
      default: '#B0BEC5'
    },
    'text-halo-color': 'hsl(0, 0%, 100%)',
    'text-halo-blur': 1,
    'text-opacity': 1,
    'text-halo-width': {
      base: 1,
      stops: [[12, 1.5], [13, 2.5]]
    },
    'icon-opacity': {
      base: 1,
      stops: [[8.99, 0], [9, 1], [12.99, 1]]
    },
    'text-translate': ['interpolate', ['linear'], ['zoom'], 12, ['literal', [0, 10]], 13, ['literal', [0, 0]]]
  }
};
const airspaceSmallBorder: LineLayer = {
  id: 'airspace-small-border',
  type: 'line',
  source: 'airspaces',
  filter: ['all', ['==', '$type', 'Polygon'], ['<', 'area', 0.02], ['!in', 'airspace_type', 'FAA']],
  paint: {
    'line-width': {
      base: 1,
      stops: [
        [9, 0.5],
        [13, 2]
      ]
    },
    'line-opacity': {
      base: 1,
      stops: [
        [9, 0],
        [13, 1]
      ]
    },
    'line-color': {
      base: 1,
      type: 'categorical',
      property: 'style_category',
      stops: [
        ['controlled', '#2962FF'],
        ['env', '#4CAF50'],
        ['restricted', '#FF4081'],
        ['airport', '#37474F'],
        ['misc', '#37474F']
      ],
      default: '#B0BEC5'
    }
  }
};
const airspaceSmallBorderBand: LineLayer = {
  id: 'airspace-small-border-band',
  type: 'line',
  source: 'airspaces',
  filter: ['all', ['==', '$type', 'Polygon'], ['<', 'area', 0.02], ['!in', 'airspace_type', 'FAA']],
  paint: {
    'line-color': {
      base: 1,
      type: 'categorical',
      property: 'style_category',
      stops: [
        ['controlled', '#2962FF'],
        ['env', '#4CAF50'],
        ['restricted', '#FF4081'],
        ['airport', '#37474F'],
        ['misc', '#37474F']
      ],
      default: '#B0BEC5'
    },
    'line-width': {
      base: 1,
      stops: [
        [9, 0],
        [13, 10]
      ]
    },
    'line-blur': 0,
    'line-gap-width': 0,
    'line-opacity': {
      base: 1,
      stops: [
        [9, 0],
        [13, 0.15]
      ]
    },
    'line-offset': {
      base: 1,
      stops: [
        [9, 0],
        [13, 5]
      ]
    }
  }
};

const airspaceFAABorder: LineLayer = {
  id: 'airspace-faa-border',
  type: 'line',
  source: 'airspaces',
  filter: ['all', ['==', '$type', 'Polygon'], ['all', ['==', 'airspace_type', 'FAA']]],
  layout: {
    visibility: 'visible'
  },
  paint: {
    'line-color': '#2962FF',
    'line-width': {
      base: 1,
      stops: [[7, .5], [13, 2]]
    },
    'line-gap-width': 0,
    'line-dasharray': [2, 2],
    'line-opacity': {
      base: 1,
      stops: [[7, 0], [9, 1]]
    }
  }
};
const airspaceTRALargeBorder: LineLayer = {
  id: 'airspace-tra-large-border',
  type: 'line',
  source: 'airspaces',
  filter: ['all', ['==', '$type', 'Polygon'], ['all', ['in', 'airspace_type', 'TRA', 'TRSA'], ['>=', 'area', .14]]],
  layout: {
    visibility: 'visible',
    'line-miter-limit': 2
  },
  paint: {
    'line-color': '#FF4081',
    'line-width': {
      base: 1,
      stops: [[7, .75], [9, 1]]
    },
    'line-gap-width': 0,
    'line-opacity': 1,
    'line-dasharray': ['step', ['zoom'], ['literal', [1, 0]], 12, ['literal', [5, 5]]]
  }
};
const airspaceTRAMiddleBorder: LineLayer = {
  id: 'airspace-tra-mid-border',
  type: 'line',
  source: 'airspaces',
  filter: ['all', ['==', '$type', 'Polygon'], ['all', ['<', 'area', .14], ['in', 'airspace_type', 'TRA', 'TRSA'], ['>=', 'area', .02]]],
  layout: {
    visibility: 'visible'
  },
  paint: {
    'line-color': '#FF4081',
    'line-width': {
      base: 1,
      stops: [[5, .25], [11, 1]]
    },
    'line-gap-width': 0,
    'line-opacity': 1,
    'line-dasharray': ['step', ['zoom'], ['literal', [1, 0]], 12, ['literal', [5, 2]]]
  }
};
const airspaceTRASmallBorder: LineLayer = {
  id: 'airspace-tra-small-border',
  type: 'line',
  source: 'airspaces',
  filter: ['all', ['==', '$type', 'Polygon'], ['all', ['<', 'area', .02], ['in', 'airspace_type', 'TRA', 'TRSA']]],
  layout: {
    visibility: 'visible'
  },
  paint: {
    'line-color': '#FF4081',
    'line-width': {
      base: 1,
      stops: [[11, .5], [13, 1]]
    },
    'line-gap-width': 0,
    'line-opacity': {
      base: 1,
      stops: [[7, 0], [9, 1]]
    },
    'line-dasharray': ['step', ['zoom'], ['literal', [1, 0]], 12, ['literal', [5, 2]]]
  }
};

export const layers = [
  airspaceLargeFill,
  airspaceLargeBorder,
  airspaceLargeBorderBand,
  airspaceLargeLabel,
  airspaceMiddleFill,
  airspaceMiddleBorder,
  airspaceMiddleBorderBand,
  airspaceMiddleLabel,
  airspaceSmallFill,
  airspaceSmallBorder,
  airspaceSmallBorderBand,
  airspaceSmallLabel,
  airspaceFAABorder,
  airspaceTRALargeBorder,
  airspaceTRAMiddleBorder,
  airspaceTRASmallBorder
];
